import type { Ref } from 'vue';
import { throttle } from 'lodash-es';
import { computed, ref, watch } from 'vue';

interface UseMermaidOptions {
  id?: string;
  theme?: 'default' | 'dark' | 'forest' | 'neutral' | string;
  config?: any;
}

async function loadMermaid() {
  if (typeof window === 'undefined') return null;
  const mermaidModule = await import('mermaid');
  return mermaidModule.default;
}

let mermaidContainer: HTMLElement | null = null;

function getMermaidContainer(): HTMLElement {
  if (!mermaidContainer) {
    mermaidContainer = document.querySelector(
      '.elx-markdown-mermaid-container'
    ) as HTMLElement;
    if (!mermaidContainer) {
      mermaidContainer = document.createElement('div') as HTMLElement;
      mermaidContainer.ariaHidden = 'true';
      mermaidContainer.style.maxHeight = '0';
      mermaidContainer.style.opacity = '0';
      mermaidContainer.style.overflow = 'hidden';
      mermaidContainer.classList.add('elx-markdown-mermaid-container');
      document.body.append(mermaidContainer);
    }
  }
  return mermaidContainer;
}

export function useMermaid(
  content: string | Ref<string>,
  options: UseMermaidOptions = {}
) {
  const { id = 'mermaid', theme = 'default', config = {} } = options;

  // 默认配置
  const mermaidConfig = computed(() => ({
    suppressErrorRendering: true,
    startOnLoad: false,
    securityLevel: 'loose',
    theme,
    ...config
  }));

  // 响应式 SVG 渲染结果
  const data = ref('');
  // 响应式错误对象，默认为 null
  const error = ref<unknown>(null);

  // 节流渲染函数，防止频繁渲染
  const throttledRender = throttle(async () => {
    const contentValue = typeof content === 'string' ? content : content.value;
    // 内容为空时，清空渲染结果和错误
    if (!contentValue?.trim()) {
      data.value = '';
      error.value = null;
      return;
    }
    try {
      // 动态加载 mermaid 库
      const mermaidInstance = await loadMermaid();
      if (!mermaidInstance) {
        data.value = contentValue;
        error.value = null;
        return;
      }
      // 语法校验
      const isValid = await mermaidInstance.parse(contentValue);
      if (!isValid) {
        console.log('Mermaid parse error: Invalid syntax');
        data.value = '';
        error.value = new Error('Mermaid parse error: Invalid syntax');
        return;
      }
      // 初始化 mermaid 配置
      mermaidInstance.initialize(mermaidConfig.value);
      // 生成唯一渲染 ID
      const renderId = `${id}-${Math.random().toString(36).substr(2, 9)}`;
      // 获取渲染容器
      const container = getMermaidContainer();
      // 渲染 SVG
      const { svg } = await mermaidInstance.render(
        renderId,
        contentValue,
        container
      );
      data.value = svg;
      error.value = null;
    } catch (err) {
      console.log('Mermaid render error:', err);
      data.value = '';
      error.value = err;
    }
  }, 300, { trailing: true, leading: true });

  // 监听内容变化，自动触发渲染
  watch(
    () => (typeof content === 'string' ? content : content.value),
    () => {
      throttledRender();
    },
    { immediate: true }
  );

  // 返回响应式对象，包含渲染结果和错误
  return {
    data,
    error
  };
}
